import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../lib/useAuthFetch';
import { Card, SectionLabel, Pill } from '../components/ui';
import AlertRules from '../components/AlertRules';
import { currentPushSubscription, enablePushOnDevice, pushAvailable } from '../lib/pushNotifications';

const API = import.meta.env.VITE_API_URL ?? '';

type Panel = 'alerts' | 'delivery' | 'thresholds' | 'locations' | 'rules';

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div
      onClick={() => !disabled && onChange(!on)}
      style={{
        width: 40, height: 22,
        background: on ? 'var(--accent)' : 'var(--border-2)',
        borderRadius: 11, position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.25s', flexShrink: 0,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: on ? 21 : 3,
        width: 16, height: 16, background: '#fff', borderRadius: '50%',
        transition: 'left 0.25s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function Seg({ options, selected, onChange }: { options: string[]; selected: number; onChange: (i: number) => void }) {
  return (
    <div style={{ display: 'flex', background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: 2, gap: 2 }}>
      {options.map((opt, i) => (
        <div key={opt} onClick={() => onChange(i)} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: selected === i ? '#fff' : 'var(--text-2)', background: selected === i ? 'var(--accent)' : 'transparent', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' as const, userSelect: 'none' as const }}>
          {opt}
        </div>
      ))}
    </div>
  );
}

const selStyle: React.CSSProperties = {
  padding: '6px 26px 6px 10px', borderRadius: 'var(--r-sm)',
  border: '1.5px solid var(--border-2)', background: 'var(--surface)',
  color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 12, cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none', outline: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='none' stroke='%238aafc8' stroke-width='2.5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '12px',
};

const numStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)',
  background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'var(--mono)',
  fontSize: 12, width: 70, textAlign: 'center', outline: 'none',
};

function Row({ label, desc, right, extra }: { label: string; desc?: string; right?: React.ReactNode; extra?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 2 }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>{desc}</div>}
        {extra}
      </div>
      {right && <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>{right}</div>}
    </div>
  );
}

/** Settings panel — a shared Card surface with an Aurora+ header (title / sub / optional action). */
function Panel({ title, sub, children, action }: { title: string; sub: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, background: 'var(--surface-2)' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginBottom: 2 }}>{title}</div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>{sub}</div>
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function AlertGrid({ rows, values, onChange }: { rows: { label: string; desc: string; badge?: string; }[]; values: boolean[]; onChange: (i: number, v: boolean) => void }) {
  return (
    // Was a hard 1fr 1fr: each cell held a label AND a switch in ~165px once
    // the content column narrowed. .l-pairs is auto-fit/minmax(340px).
    <div className="l-pairs">
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 20px', borderBottom: i < rows.length - 2 ? '1px solid var(--border)' : (rows.length % 2 === 0 && i >= rows.length - 2 ? 'none' : '1px solid var(--border)') }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" style={{ width: 13, height: 13 }}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
              </div>
              {row.label}
              {row.badge && <Pill tone="accent" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>{row.badge}</Pill>}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 1, paddingLeft: 34 }}>{row.desc}</div>
          </div>
          <Toggle on={values[i]} onChange={v => onChange(i, v)} />
        </div>
      ))}
    </div>
  );
}

const DIGEST_OPTIONS = ['realtime', 'daily', 'weekly'] as const;

export default function NotificationSettings() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const [panel, setPanel] = useState<Panel>('alerts');
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceEnrolled, setDeviceEnrolled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const [sharedAlerts, setSharedAlerts] = useState([true, true, true, true]);
  const [generalAlerts, setGeneralAlerts] = useState([true, false, false, true]);

  const [emailOn, setEmailOn] = useState(true);
  const [pushOn, setPushOn] = useState(true);
  const [sharedDigest, setSharedDigest] = useState(0);
  const [generalDigest, setGeneralDigest] = useState(1);
  const [quietOn, setQuietOn] = useState(true);
  const [quietWeekend, setQuietWeekend] = useState(false);

  const [priceThreshold, setPriceThreshold] = useState(5);
  const [dollarThreshold, setDollarThreshold] = useState(2);
  const [maxAlerts, setMaxAlerts] = useState(25);
  const [cooldown, setCooldown] = useState('1 hour');

  useEffect(() => {
    authFetch(`${API}/api/v1/settings/notifications`)
      .then((r) => r.json())
      .then((res) => {
        if (!res.success) return;
        const d = res.data;
        if (d.email_enabled != null) setEmailOn(d.email_enabled);
        if (d.push_enabled != null) setPushOn(d.push_enabled);
        if (d.price_threshold_pct != null) setPriceThreshold(d.price_threshold_pct);
        if (d.digest_frequency) {
          const idx = DIGEST_OPTIONS.indexOf(d.digest_frequency);
          if (idx >= 0) { setSharedDigest(idx); setGeneralDigest(idx); }
        }
        if (d.quiet_hours_start) setQuietOn(true);
        if (Array.isArray(d.alert_types)) {
          setSharedAlerts([
            d.alert_types.includes('price_drop'),
            d.alert_types.includes('price_increase'),
            d.alert_types.includes('new_promo'),
            d.alert_types.includes('new_sku'),
          ]);
          setGeneralAlerts([
            d.alert_types.includes('new_competitor'),
            d.alert_types.includes('new_sku'),
            d.alert_types.includes('sku_removed'),
            d.alert_types.includes('promo_ended'),
          ]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    currentPushSubscription().then((sub) => setDeviceEnrolled(!!sub)).catch(() => {})
  }, []);

  const enrollDevice = async () => {
    setPushBusy(true)
    try {
      await enablePushOnDevice(authFetch, API)
      setDeviceEnrolled(true)
      setPushOn(true)
      setDirty(true)
      showToast('Push alerts enabled on this device')
    } catch (err: any) {
      showToast(err?.message || 'Could not enable push alerts')
    } finally {
      setPushBusy(false)
    }
  };

  const handleSave = useCallback(async () => {
    try {
      const alertTypes: string[] = [];
      if (sharedAlerts[0]) alertTypes.push('price_drop');
      if (sharedAlerts[1]) alertTypes.push('price_increase');
      if (sharedAlerts[2]) alertTypes.push('new_promo');
      // 'new_sku' is surfaced by two checkboxes (shared[3] + general[1]); persist if either is on
      if (sharedAlerts[3] || generalAlerts[1]) alertTypes.push('new_sku');
      if (generalAlerts[0]) alertTypes.push('new_competitor');
      if (generalAlerts[2]) alertTypes.push('sku_removed');
      if (generalAlerts[3]) alertTypes.push('promo_ended');
      await authFetch(`${API}/api/v1/settings/notifications`, {
        method: 'PATCH',
        body: JSON.stringify({
          email_enabled: emailOn,
          push_enabled: pushOn,
          price_threshold_pct: priceThreshold,
          digest_frequency: DIGEST_OPTIONS[sharedDigest] ?? 'realtime',
          alert_types: alertTypes,
        }),
      });
      setDirty(false);
      showToast('Changes saved');
    } catch {
      showToast('Failed to save — please try again');
    }
  }, [emailOn, pushOn, priceThreshold, sharedDigest, sharedAlerts, generalAlerts]);

  const mark = () => setDirty(true);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const navItems: { id: Panel; label: string }[] = [
    { id: 'alerts', label: 'Alert types' },
    { id: 'delivery', label: 'Delivery & digest' },
    { id: 'thresholds', label: 'Thresholds' },
    { id: 'locations', label: 'Per-location overrides' },
  ];

  const sharedRows = [
    { label: 'Rival price drop on shared brand', desc: 'Rival drops price on a brand you both carry', badge: 'Priority' },
    { label: 'Rival price increase on shared brand', desc: 'Opportunity to reprice or hold advantage' },
    { label: 'Rival flash sale on shared brand', desc: 'Time-limited sale on a brand you both carry', badge: 'Priority' },
    { label: 'Rival adds a brand you carry', desc: 'A rival starts stocking a brand you already carry', badge: 'New' },
  ];

  const generalRows = [
    { label: 'New rival competitor detected', desc: 'New dispensary opens in your market area' },
    { label: 'Rival new SKUs added', desc: 'Rivals expand their catalog with new products' },
    { label: 'Rival SKUs removed', desc: "Products dropped from a rival's active menu" },
    { label: 'Rival recurring deal changes', desc: 'Daily deals or happy hours added or changed' },
  ];

  const locOverrides = [
    { name: 'WeHo Flagship', market: 'Elite · WeHo', email: true, push: true, digest: 'Real-time', quiet: 'Global', threshold: 'Global' },
    { name: 'DTLA Flagship', market: 'Hot · DTLA', email: true, push: false, digest: 'Daily', quiet: 'Off', threshold: 'Global' },
    { name: 'SoMa San Francisco', market: 'Elite · SoMa SF', email: true, push: true, digest: 'Real-time', quiet: 'Custom', threshold: '3%' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)' }}>
      {/* Topbar */}
      <div style={{ background: 'var(--surface)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', padding: '0 28px', flexShrink: 0 }}>
        <div className="l-wrap" style={{ gap: 14, padding: '12px 0 11px' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none', flexShrink: 0, padding: 0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div style={{ width: 1, height: 16, background: 'var(--border-2)', flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 }}>Notification Settings</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginTop: 3 }}>Global preferences · applies to all locations</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn" style={{ fontSize: 11, padding: '7px 13px' }} onClick={() => { setDirty(false); showToast('Reset to defaults'); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></svg>
              Reset to defaults
            </button>
            <button className="btn btn-primary" style={{ fontSize: 11, padding: '7px 13px' }} onClick={handleSave}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><polyline points="20 6 9 17 4 12" /></svg>
              Save preferences
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px clamp(14px, 3vw, 28px)' }}>
        {/* Was a hard 260px 1fr grid with a sticky sidebar and no reflow. At
            743px that left the content column ~339px, which is where the
            clipping came from. */}
        <div className="l-shell">

          {/* Left nav */}
          <div className="l-shell-nav" style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 0 }}>
            <SectionLabel className="l-shell-nav-label" style={{ padding: '0 12px 8px' }}>Global preferences</SectionLabel>
            {navItems.map(item => (
              <div key={item.id} onClick={() => setPanel(item.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: panel === item.id ? 600 : 500, color: panel === item.id ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer', background: panel === item.id ? 'var(--surface)' : 'transparent', border: panel === item.id ? '1px solid var(--border)' : '1px solid transparent', boxShadow: panel === item.id ? 'var(--card-shadow)' : 'none', userSelect: 'none' as const }}>
                {item.label}
              </div>
            ))}

            {/* Per-rule automation — deliberately separated from the global toggles above */}
            <div style={{ height: 1, background: 'var(--border)', margin: '10px 0 8px' }} />
            <SectionLabel className="l-shell-nav-label" style={{ padding: '0 12px 8px' }}>Automation</SectionLabel>
            <div data-testid="nav-alert-rules" onClick={() => setPanel('rules')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: panel === 'rules' ? 600 : 500, color: panel === 'rules' ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer', background: panel === 'rules' ? 'var(--surface)' : 'transparent', border: panel === 'rules' ? '1px solid var(--border)' : '1px solid transparent', boxShadow: panel === 'rules' ? 'var(--card-shadow)' : 'none', userSelect: 'none' as const }}>
              Alert rules
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />
            <div className="l-shell-nav-hint" style={{ fontSize: 11, color: 'var(--text-3)', padding: '6px 12px', lineHeight: 1.5 }}>
              Global preferences apply everywhere. Alert rules fire on specific competitive changes to the people you pick.
            </div>
          </div>

          {/* Right panels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* PANEL: Alert Types */}
            {panel === 'alerts' && (
              <>
                <Panel title="Shared brand alerts" sub="Alerts for price moves on brands you and a rival both carry. Highest priority — always recommended on.">
                  <AlertGrid rows={sharedRows} values={sharedAlerts} onChange={(i, v) => { const n = [...sharedAlerts]; n[i] = v; setSharedAlerts(n); mark(); }} />
                </Panel>
                <Panel title="General alerts" sub="Broader market activity alerts. Lower priority than shared brand alerts.">
                  <AlertGrid rows={generalRows} values={generalAlerts} onChange={(i, v) => { const n = [...generalAlerts]; n[i] = v; setGeneralAlerts(n); mark(); }} />
                </Panel>
              </>
            )}

            {/* PANEL: Delivery */}
            {panel === 'delivery' && (
              <>
                <Panel title="Delivery channels" sub="Where you receive alerts. Email is live now; SMS will be enabled at launch.">
                  <Row label="Email alerts" desc="patrick@cannaspy.com · Sends to your account email" right={<Toggle on={emailOn} onChange={v => { setEmailOn(v); mark(); }} />} />
                  <Row label="Push notifications" desc={deviceEnrolled ? 'This device is enrolled for assignment and market alerts' : 'Enable once on each phone or computer that should receive alerts'} right={<>
                    <button className="btn btn-ghost" disabled={pushBusy || !pushAvailable()} onClick={enrollDevice}>{pushBusy ? 'Enabling…' : deviceEnrolled ? 'Device enabled' : 'Enable this device'}</button>
                    <Toggle on={pushOn} onChange={v => { setPushOn(v); mark(); }} />
                  </>} />
                  <Row label="SMS / text alerts" desc="Coming soon — not live before launch." right={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Pill tone="neutral" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Coming soon</Pill>
                      <Toggle on={false} onChange={() => {}} disabled />
                    </div>
                  } />
                  <Row label="Slack notifications" desc="Post alerts to a Slack channel — coming soon" right={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Pill tone="accent" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Soon</Pill>
                      <Toggle on={false} onChange={() => {}} disabled />
                    </div>
                  } />
                </Panel>

                <Panel title="Digest frequency" sub="How often alerts are batched and sent. Real-time fires immediately — recommended for shared brand alerts.">
                  <Row label="Shared brand alerts" desc="Price moves and flash sales on brands you both carry — act fast" right={<Seg options={['Real-time', 'Daily', 'Weekly']} selected={sharedDigest} onChange={v => { setSharedDigest(v); mark(); }} />} />
                  <Row label="General alerts" desc="SKU changes, new rivals, recurring deal updates" right={<Seg options={['Real-time', 'Daily', 'Weekly']} selected={generalDigest} onChange={v => { setGeneralDigest(v); mark(); }} />} />
                </Panel>

                <Panel title="Quiet hours" sub="No alerts sent during this window. Monitoring still runs — alerts queue and deliver when quiet hours end.">
                  <Row label="Enable quiet hours" desc="Silence alerts overnight and on weekends" right={<Toggle on={quietOn} onChange={v => { setQuietOn(v); mark(); }} />} />
                  {quietOn && (
                    <>
                      <Row label="Quiet window" desc="Local time · Alerts queued and released at end of window" right={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <select defaultValue="10:00 PM" style={{ ...selStyle, width: 100 }} onChange={mark}><option>10:00 PM</option><option>9:00 PM</option><option>8:00 PM</option><option>11:00 PM</option></select>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>to</span>
                          <select defaultValue="7:00 AM" style={{ ...selStyle, width: 100 }} onChange={mark}><option>7:00 AM</option><option>6:00 AM</option><option>8:00 AM</option><option>9:00 AM</option></select>
                        </div>
                      } />
                      <Row label="Include weekends" desc="Extend quiet hours to Saturday and Sunday" right={<Toggle on={quietWeekend} onChange={v => { setQuietWeekend(v); mark(); }} />} />
                    </>
                  )}
                </Panel>
              </>
            )}

            {/* PANEL: Thresholds */}
            {panel === 'thresholds' && (
              <>
                <Panel title="Price change threshold" sub="Only alert when a price changes by at least this percentage. Filters out penny-rounding noise.">
                  <Row label="Minimum price change" desc="Applies to all price-related alerts. Shared brand alerts ignore this — they always fire." right={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={priceThreshold} min={1} max={50} onChange={e => { setPriceThreshold(+e.target.value); mark(); }} style={numStyle} />
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>% change</span>
                    </div>
                  } />
                  <Row label="Minimum dollar change" desc="Also require at least this dollar amount — prevents alerts on $0.50 moves" right={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>$</span>
                      <input type="number" value={dollarThreshold} min={0} max={20} onChange={e => { setDollarThreshold(+e.target.value); mark(); }} style={numStyle} />
                    </div>
                  } />
                </Panel>
                <Panel title="Alert volume control" sub="Prevents alert fatigue when monitoring detects widespread market movement.">
                  <Row label="Max alerts per day" desc="Cap total daily alerts across all locations. Prioritizes shared brand alerts first." right={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" value={maxAlerts} min={5} max={200} onChange={e => { setMaxAlerts(+e.target.value); mark(); }} style={numStyle} />
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>alerts/day</span>
                    </div>
                  } />
                  <Row label="Cooldown between same-rival alerts" desc="Don't fire another alert on the same rival within this window" right={
                    <select value={cooldown} onChange={e => { setCooldown(e.target.value); mark(); }} style={selStyle}>
                      <option>No cooldown</option><option>1 hour</option><option>4 hours</option><option>24 hours</option>
                    </select>
                  } />
                </Panel>
              </>
            )}

            {/* PANEL: Alert rules (per-rule automation) */}
            {panel === 'rules' && <AlertRules />}

            {/* PANEL: Per-location overrides */}
            {panel === 'locations' && (
              <Panel title="Per-location overrides" sub="These settings override the global defaults for specific locations. Leave a toggle off to inherit the global setting." action={
                <button className="btn" style={{ fontSize: 11 }} onClick={() => showToast('Override dialog coming soon')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                  Add override
                </button>
              }>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Location', 'Email', 'SMS (launch)', 'Push', 'Digest', 'Quiet hours', 'Threshold'].map(h => (
                          <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: 'var(--text-3)', padding: '10px 16px', borderBottom: '1px solid var(--border)', textAlign: h === 'Location' ? 'left' as const : 'center' as const, fontWeight: 700, background: 'var(--surface-3)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {locOverrides.map((loc, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 3 }}>{loc.name}</div>
                            <Pill tone="blocking" variant="chip" style={{ letterSpacing: '0.06em' }}>{loc.market}</Pill>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Toggle on={loc.email} onChange={() => mark()} />
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <Pill tone="neutral" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>Launch</Pill>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Toggle on={loc.push} onChange={() => mark()} />
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <select defaultValue={loc.digest} onChange={mark} style={{ ...selStyle, width: 100 }}>
                              <option>Real-time</option><option>Daily</option><option>Weekly</option>
                            </select>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <select defaultValue={loc.quiet} onChange={mark} style={{ ...selStyle, width: 90 }}>
                              <option>Global</option><option>Off</option><option>Custom</option>
                            </select>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' as const }}>
                            <select defaultValue={loc.threshold} onChange={mark} style={{ ...selStyle, width: 90 }}>
                              <option>Global</option><option>3%</option><option>10%</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ opacity: 0.5 }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>+ 7 more locations</div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>Using global defaults</div>
                        </td>
                        <td colSpan={6} style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                          Inheriting all global settings · Click to expand
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

          </div>
        </div>
      </div>

      {/* Save bar — global preferences only. Alert rules manage their own saves
          (per-rule, in the modal), so the global bar is hidden on that panel. */}
      {panel !== 'rules' && (
      <div style={{ position: 'sticky', bottom: 0, background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: dirty ? 'var(--warm)' : 'var(--accent)', flexShrink: 0 }} />
          {dirty ? 'Unsaved changes' : 'All changes saved'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={() => { setDirty(false); showToast('Changes discarded'); }}>Discard changes</button>
          <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={handleSave}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><polyline points="20 6 9 17 4 12" /></svg>
            Save preferences
          </button>
        </div>
      </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--text-1)', color: 'var(--surface)', padding: '9px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'var(--mono)', zIndex: 9000, letterSpacing: '0.04em' }}>
          {toast}
        </div>
      )}
    </div>
  );
}
